import { useRef, useState } from "react";
import PageLayout from "@/components/PageLayout";
import heroAsset from "@/assets/header-totally-26-27-v3.webp.asset.json";
import { Button } from "@/components/ui/button";
import { ArrowRight, Truck, Palette, FileArrowUp, Upload } from "@/components/Icon";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import TotallyAanbod, {
  TOTALLY_PRODUCTS,
  ColorDot,
  type TotallyProductColor,
} from "@/components/totally/TotallyAanbod";

import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { trackEvent } from "@/lib/analytics";


const HERO = heroAsset.url;



// Kleuren komen uit dezelfde centrale dataset als de productcards.
const productColors = (key: "hoodie" | "sweater" | "beanie"): TotallyProductColor[] =>
  TOTALLY_PRODUCTS.find((p) => p.key === key)?.colors ?? [];

const sortColors = (colors: TotallyProductColor[]) =>
  [...colors].sort((a, b) => a.name.localeCompare(b.name, "nl", { sensitivity: "base" }));

const HOODIE_COLORS = sortColors(productColors("hoodie"));
const SWEATER_COLORS = sortColors(productColors("sweater"));
const BEANIE_COLORS = sortColors(productColors("beanie"));

function ColorSelect({
  value,
  onChange,
  colors,
  triggerClassName,
}: {
  value: string;
  onChange: (v: string) => void;
  colors: TotallyProductColor[];
  triggerClassName?: string;
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className={triggerClassName}>
        <SelectValue placeholder="- Select -" />
      </SelectTrigger>
      <SelectContent className="max-h-72">
        {colors.map((c) => (
          <SelectItem key={c.name} value={c.name} className="py-2.5">
            <span className="flex items-center gap-2.5">
              <ColorDot color={c} />
              <span>{c.name}</span>
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

const SIZES = ["S", "M", "L", "XL", "XXL"] as const;

type SizeQty = Record<string, number>;

type ProductKey = "hoodie" | "sweater" | "beanie";

interface ColorRow {
  id: string;
  color: string;
  quantities: SizeQty;
}

const newRow = (): ColorRow => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  color: "",
  quantities: {},
});

/** Kleurregels met eigen aantallen per maat (of één aantal bij one-size producten). */
function ColorRowsBlock({
  rows,
  colors,
  oneSize,
  onChange,
}: {
  rows: ColorRow[];
  colors: TotallyProductColor[];
  oneSize?: boolean;
  onChange: (rows: ColorRow[]) => void;
}) {
  const update = (id: string, patch: Partial<ColorRow>) =>
    onChange(rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  return (
    <div className="space-y-4">
      {rows.map((row, i) => {
        const taken = rows.filter((r) => r.id !== row.id).map((r) => r.color);
        const available = colors.filter((c) => !taken.includes(c.name));
        return (
          <div key={row.id} className={i > 0 ? "pt-4 border-t border-border" : ""}>
            <div className="flex items-end gap-2">
              <div className="flex-1 min-w-0 space-y-2">
                <Label>Kleur {rows.length > 1 ? i + 1 : ""}</Label>
                <ColorSelect
                  value={row.color}
                  onChange={(v) => update(row.id, { color: v })}
                  colors={available}
                />
              </div>
              {i > 0 && (
                <button
                  type="button"
                  onClick={() => onChange(rows.filter((r) => r.id !== row.id))}
                  className="h-10 px-2 text-xs text-muted-foreground hover:text-destructive shrink-0"
                  aria-label={`Kleur ${i + 1} verwijderen`}
                >
                  Verwijderen
                </button>
              )}
            </div>

            {oneSize ? (
              <div className="mt-3 max-w-[160px]">
                <Label className="text-xs">Aantal</Label>
                <Input
                  type="number"
                  min={0}
                  value={row.quantities.qty || ""}
                  onChange={(e) =>
                    update(row.id, { quantities: { qty: parseInt(e.target.value || "0", 10) } })
                  }
                  className="mt-1 text-center"
                />
              </div>
            ) : (
              <div className="grid grid-cols-5 gap-2 mt-3">
                {SIZES.map((sz) => (
                  <div key={sz} className="flex flex-col items-center">
                    <Label className="text-xs mb-1">{sz}</Label>
                    <Input
                      type="number"
                      min={0}
                      value={row.quantities[sz] || ""}
                      onChange={(e) =>
                        update(row.id, {
                          quantities: {
                            ...row.quantities,
                            [sz]: parseInt(e.target.value || "0", 10),
                          },
                        })
                      }
                      className="text-center"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {rows.length < colors.length && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onChange([...rows, newRow()])}
        >
          + Nog een kleur toevoegen
        </Button>
      )}
    </div>
  );
}


const TotallyStudentenreizen = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);

  // form state
  const [products, setProducts] = useState<Record<ProductKey, boolean>>({
    hoodie: false,
    sweater: false,
    beanie: false,
  });
  const [colorRows, setColorRows] = useState<Record<ProductKey, ColorRow[]>>({
    hoodie: [],
    sweater: [],
    beanie: [],
  });
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleLogoSelect = (file: File | null) => {
    if (file && file.size > 4 * 1024 * 1024) {
      toast({ title: "Logo te groot", description: "Maximaal 4MB.", variant: "destructive" });
      setLogoFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    setLogoFile(file);
  };


  const toggleProduct = (key: ProductKey, checked: boolean) => {
    setProducts((p) => ({ ...p, [key]: checked }));
    setColorRows((r) => ({ ...r, [key]: checked ? [newRow()] : [] }));
  };

  const setRows = (key: ProductKey) => (rows: ColorRow[]) =>
    setColorRows((r) => ({ ...r, [key]: rows }));


  const sizeTotal = (s: SizeQty) =>
    Object.values(s).reduce((a, b) => a + (Number(b) || 0), 0);

  const sizesToString = (s: SizeQty) =>
    Object.entries(s)
      .filter(([, v]) => v > 0)
      .map(([k, v]) => `${k}:${v}`)
      .join(", ");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    const form = e.currentTarget;
    const fd = new FormData(form);

    const lines: string[] = [];
    let totalQty = 0;

    const labels: Record<ProductKey, string> = {
      hoodie: "Hoodie",
      sweater: "Sweater",
      beanie: "Beanie",
    };
    (["hoodie", "sweater", "beanie"] as ProductKey[]).forEach((key) => {
      if (!products[key]) return;
      const oneSize = key === "beanie";
      colorRows[key].forEach((row) => {
        const q = sizeTotal(row.quantities);
        if (q === 0 && !row.color) return;
        totalQty += q;
        lines.push(
          oneSize
            ? `${labels[key]} — Kleur: ${row.color || "n.t.b."} — Aantal: ${q}`
            : `${labels[key]} — Kleur: ${row.color || "n.t.b."} — Maten: ${sizesToString(row.quantities)} (totaal ${q})`,
        );
      });
    });


    if (lines.length === 0) {
      setLoading(false);
      toast({
        title: "Geen producten geselecteerd",
        description: "Selecteer minimaal één product.",
        variant: "destructive",
      });
      return;
    }

    let logoPath: string | null = null;
    if (logoFile) {
      const path = `quote-logos/totally-${Date.now()}-${logoFile.name}`;
      const { error: upErr } = await supabase.storage.from("logos").upload(path, logoFile);
      if (!upErr) {
        logoPath = path;
      }
    }

    const remarks = [
      `Totally Studentenreizen aanvraag`,
      `Vereniging: ${fd.get("vereniging") || ""}`,
      `Vertrekdatum: ${fd.get("vertrek") || "—"}`,
      ``,
      `Bestelling:`,
      ...lines.map((l) => `- ${l}`),
      logoPath ? `\nLogo (privé link wordt in de notificatie-mail toegevoegd)` : "",
      fd.get("opmerkingen") ? `\nOpmerkingen: ${fd.get("opmerkingen")}` : "",
    ]
      .filter(Boolean)
      .join("\n");

    const { error } = await supabase.from("quote_requests").insert({
      name: fd.get("contact") as string,
      company: (fd.get("vereniging") as string) || null,
      email: fd.get("email") as string,
      phone: fd.get("telefoon") as string,
      product_type: `Totally: ${Object.keys(products).filter((k) => products[k]).join(", ")}`,
      technique: "Full color (DTF / Transfer)",
      quantity: totalQty,
      deadline: (fd.get("vertrek") as string) || null,
      remarks,
      sizes: JSON.parse(
        JSON.stringify({
          hoodie: products.hoodie ? { colorRows: colorRows.hoodie } : null,
          sweater: products.sweater ? { colorRows: colorRows.sweater } : null,
          beanie: products.beanie ? { colorRows: colorRows.beanie } : null,
        }),
      ),
    });

    if (error) {
      console.error("totally insert failed", error);
      setLoading(false);
      toast({
        title: "Er ging iets mis",
        description: "Probeer het later opnieuw of bel ons op 070 887 3306.",
        variant: "destructive",
      });
      return;
    }

    try {
      await supabase.functions.invoke("send-form-notification", {
        body: {
          type: "studentenreizen",
          data: {
            name: fd.get("contact"),
            vereniging: fd.get("vereniging"),
            email: fd.get("email"),
            telefoon: fd.get("telefoon"),
            vertrekdatum: fd.get("vertrek"),
            bestelling: lines.join("\n"),
            opmerkingen: fd.get("opmerkingen"),
            logo_path: logoPath,
            aantal_totaal: totalQty,
          },
        },
      });
    } catch (e) {
      console.error("notification mail failed", e);
    }

    setLoading(false);

    trackEvent("offerte_verzonden", {
      product: "Totally Studentenreizen",
      aantal: totalQty,
    });

    toast({ title: "Aanvraag verstuurd!", description: "We nemen snel contact met je op." });
    form.reset();
    setProducts({ hoodie: false, sweater: false, beanie: false });
    setColorRows({ hoodie: [], sweater: [], beanie: [] });
    setLogoFile(null);
  };

  return (
    <PageLayout
      title="Totally Studentenreizen – Hoodies bestellen"
      metaDescription="Bestel jullie skitrip hoodies, sweaters en beanies met volledige bedrukking. Speciale prijzen voor Totally Studentenreizen-deelnemers."
      ogImage={HERO}
      hero={
        <section className="relative min-h-[560px] md:min-h-[780px] flex items-center overflow-hidden pt-32 md:pt-40">
          <img
            src={HERO}
            alt="Totally Studentenreizen wintersport hoodies"
            className="absolute inset-0 w-full h-full object-cover object-top"
            width={1920}
            height={1080}
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(135deg, hsla(217, 56%, 12%, 0.25), hsla(194, 66%, 18%, 0.25))",
            }}
          />
          <div className="container relative z-10 py-16 md:py-24">
            <div className="max-w-2xl space-y-4 animate-fade-up">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.1] text-surface-dark-foreground [text-shadow:0_2px_16px_rgba(0,0,0,0.5)]">
                Jullie trip. Jullie stijl.
              </h1>
              <div className="inline-flex items-center gap-2.5 rounded-full bg-white/95 px-4 py-2 text-sm md:text-base font-semibold text-[#0a2933] shadow-lg">
                <span className="h-2.5 w-2.5 rounded-full bg-primary shrink-0" aria-hidden="true" />
                Magic Print is officiële partner van Totally Studentenreizen
              </div>
              <p className="text-lg md:text-xl text-surface-dark-foreground max-w-lg [text-shadow:0_1px_10px_rgba(0,0,0,0.5)]">
                Maak jullie Totally wintersport compleet met gepersonaliseerde kleding voor de hele groep.
              </p>
              <div className="flex flex-wrap gap-4 pt-2">
                <Button size="lg" asChild>
                  <a href="#aanbod">
                    Bekijk de collectie <ArrowRight className="ml-2 w-4 h-4" />
                  </a>
                </Button>
                <Button size="lg" variant="outline" className="border-white/40 text-white bg-white/10 hover:bg-white/20" asChild>
                  <a href="#aanvraagformulier">
                    Direct bestellen
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </section>
      }
    >
      <section className="mb-10">
        <h2 className="text-2xl md:text-3xl font-bold mb-4 text-[#0a2933]">
          Jullie wintersport. Jullie kleding.
        </h2>
        <p className="text-muted-foreground mb-4">
          Ga je met Totally op wintersport? Maak jullie trip compleet met gepersonaliseerde hoodies, sweaters en beanies met jullie eigen skitrip- of verenigingslogo.
        </p>
        <p className="text-muted-foreground mb-4">
          Als deelnemer aan een Totally-reis profiteer je van speciale prijzen. De bedrukking van jullie logo is al inbegrepen, ook in full colour. En heb je nog geen compleet ontwerp? Geen probleem, we helpen jullie graag om er iets moois van te maken.
        </p>
        <p className="text-muted-foreground mb-6">
          Alle prijzen zijn inclusief btw en bedrukking.
        </p>
        <Button asChild size="lg">
          <a href="#aanvraagformulier">Direct bestellen</a>
        </Button>
      </section>


      <TotallyAanbod
        onSelectProduct={(key) => {
          setProducts((prev) => ({ ...prev, [key]: true }));
          document.getElementById("aanvraagformulier")?.scrollIntoView({ behavior: "smooth" });
        }}
      />

      <section className="pt-4 md:pt-6 pb-4 md:pb-6 bg-white">
        <div>
          <div className="mb-4 md:mb-6">
            <p className="text-sm font-semibold tracking-wider text-primary uppercase mb-2">
              GOED OM TE WETEN
            </p>
            <h2 className="text-2xl md:text-3xl font-bold text-[#0a2933] mb-2">
              Alles voor jullie bestelling
            </h2>
            <p className="text-muted-foreground text-sm md:text-base">
              Van levertijd tot bedrukking: dit moet je weten voordat je bestelt.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 items-stretch">
            <div className="flex h-full flex-col rounded-xl border border-border bg-[#fcfcfc] p-4 md:p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="flex items-center justify-center w-11 h-11 rounded-xl bg-primary/10 text-primary shrink-0">
                  <Truck className="w-5 h-5" />
                </div>
                <h3 className="text-lg font-bold text-[#0a2933]">Levering & betaling</h3>
              </div>
              <div className="space-y-3">
                <p className="text-lg md:text-xl font-bold text-[#0a2933]">2–3 weken</p>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>Levertijd</li>
                  <li>Spoed? Even overleggen.</li>
                  <li>Betaling vooraf</li>
                  <li>Incl. btw · excl. verzendkosten</li>
                </ul>
              </div>
              <div className="mt-auto pt-5">
                <Button variant="outline" className="w-full" asChild>
                  <a href="#aanvraagformulier">Direct bestellen</a>
                </Button>
              </div>
            </div>

            <div className="flex h-full flex-col rounded-xl border border-border bg-[#fcfcfc] p-4 md:p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="flex items-center justify-center w-11 h-11 rounded-xl bg-primary/10 text-primary shrink-0">
                  <Palette className="w-5 h-5" />
                </div>
                <h3 className="text-lg font-bold text-[#0a2933]">Jullie bedrukking</h3>
              </div>
              <div className="space-y-3">
                <p className="text-lg md:text-xl font-bold text-[#0a2933]">Full colour inbegrepen</p>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>Totally Snow-logo op de borst</li>
                  <li>Jullie eigen ontwerp op de rug</li>
                </ul>
              </div>
              <div className="mt-auto pt-5">
                <Button variant="outline" className="w-full" asChild>
                  <a href="#aanvraagformulier">Bekijk mogelijkheden</a>
                </Button>
              </div>
            </div>

            <div className="flex h-full flex-col rounded-xl border border-border bg-[#fcfcfc] p-4 md:p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="flex items-center justify-center w-11 h-11 rounded-xl bg-primary/10 text-primary shrink-0">
                  <FileArrowUp className="w-5 h-5" />
                </div>
                <h3 className="text-lg font-bold text-[#0a2933]">Logo aanleveren</h3>
              </div>
              <div className="space-y-3">
                <p className="text-lg md:text-xl font-bold text-[#0a2933]">EPS, PDF of AI</p>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>Pixelbestand minimaal 300 dpi</li>
                  <li>Hulp nodig? Wij helpen graag met het ontwerp.</li>
                </ul>
              </div>
              <div className="mt-auto pt-5">
                <Button variant="outline" className="w-full" asChild>
                  <a href="mailto:info@magicprint.nl">Logo versturen</a>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div id="aanvraagformulier" className="scroll-mt-32">
        <h2 className="text-2xl md:text-3xl font-bold mb-6 text-[#0a2933]">Aanvraagformulier</h2>
        <form onSubmit={handleSubmit} className="space-y-6 p-6 rounded-2xl border border-border bg-card">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="vereniging">Naam vereniging *</Label>
              <Input id="vereniging" name="vereniging" required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact">Naam contactpersoon *</Label>
              <Input id="contact" name="contact" required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">E-mailadres *</Label>
              <Input id="email" name="email" type="email" required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="telefoon">Telefoonnummer *</Label>
              <Input id="telefoon" name="telefoon" type="tel" required />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="vertrek">Vertrekdatum reis</Label>
              <Input id="vertrek" name="vertrek" type="date" />
            </div>
          </div>

          <div>
            <Label className="block mb-3 text-base font-semibold">Kies het product</Label>
            <div className="grid grid-cols-3 gap-3">
              {(["hoodie", "sweater", "beanie"] as const).map((p) => (
                <label key={p} className="flex items-center gap-2 p-3 rounded-lg border border-border cursor-pointer hover:bg-muted/40">
                  <Checkbox
                    checked={products[p]}
                    onCheckedChange={(v) => toggleProduct(p, !!v)}
                  />
                  <span className="capitalize text-sm font-medium">{p}</span>
                </label>
              ))}
            </div>
          </div>

          {products.hoodie && (
            <div className="p-4 rounded-xl border border-border bg-background">
              <h3 className="font-bold mb-3">Hoodie</h3>
              <ColorRowsBlock
                rows={colorRows.hoodie}
                colors={HOODIE_COLORS}
                onChange={setRows("hoodie")}
              />
            </div>
          )}

          {products.sweater && (
            <div className="p-4 rounded-xl border border-border bg-background">
              <h3 className="font-bold mb-3">Sweater</h3>
              <ColorRowsBlock
                rows={colorRows.sweater}
                colors={SWEATER_COLORS}
                onChange={setRows("sweater")}
              />
            </div>
          )}

          {products.beanie && (
            <div className="p-4 rounded-xl border border-border bg-background">
              <h3 className="font-bold mb-3">Beanie</h3>
              <ColorRowsBlock
                rows={colorRows.beanie}
                colors={BEANIE_COLORS}
                oneSize
                onChange={setRows("beanie")}
              />
            </div>
          )}

          <div className="space-y-3">
            <Label htmlFor="logo">Upload hier je logo (max. 4MB)</Label>
            <p className="text-xs text-muted-foreground">
              Toegestaan: JPG, PNG, PDF, ZIP. Of stuur je drukbestand (AI/EPS) naar{" "}
              <a href="mailto:info@magicprint.nl" className="text-primary underline underline-offset-2 hover:text-primary/80">
                info@magicprint.nl
              </a>.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <input
                ref={fileInputRef}
                id="logo"
                type="file"
                accept=".jpg,.jpeg,.png,.pdf,.zip"
                onChange={(e) => handleLogoSelect(e.target.files?.[0] ?? null)}
                className="sr-only"
                aria-label="Kies een bestand om te uploaden"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="w-4 h-4 mr-2" />
                {logoFile ? "Anders bestand kiezen" : "Bladeren…"}
              </Button>
              {logoFile ? (
                <span className="text-sm text-muted-foreground">
                  Geselecteerd: <span className="font-medium text-foreground">{logoFile.name}</span>
                </span>
              ) : (
                <span className="text-sm text-muted-foreground">Geen bestand geselecteerd</span>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="opmerkingen">Opmerkingen</Label>
            <Textarea id="opmerkingen" name="opmerkingen" rows={4} />
          </div>

          <Button type="submit" size="lg" disabled={loading} className="w-full sm:w-auto">
            {loading ? "Verzenden..." : "Verzenden"}
          </Button>
        </form>
      </div>
    </PageLayout>
  );
};

export default TotallyStudentenreizen;
